import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ZoomIn, ZoomOut, Hand, Volume2, VolumeX } from 'lucide-react';
import { Slider } from '../ui/slider';

const BASE_PX_PER_SEC = 10;
const LANE_H = 88;
const RULER_H = 24;
const LIME = '#CDDC39';
const ZOOM_LEVELS = [0.5, 1, 2, 4];
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 4;
const LONG_PRESS_MS = 400;
const LONG_PRESS_MOVE_TOL = 8; // px of wobble allowed before we decide it's a scroll

function computePeaks(buffer, width, durationSec) {
  const data = buffer.getChannelData(0);
  const total = durationSec != null
    ? Math.min(Math.floor(durationSec * buffer.sampleRate), data.length)
    : data.length;
  const step = Math.max(1, Math.floor(total / width));
  const peaks = new Float32Array(width);
  for (let x = 0; x < width; x++) {
    let max = 0;
    const start = x * step;
    const end = Math.min(start + step, total);
    for (let i = start; i < end; i += 4) {
      const v = Math.abs(data[i]);
      if (v > max) max = v;
    }
    peaks[x] = max;
  }
  return peaks;
}

function drawClip(ctx2d, peaks, x, y, w, h, fill, wave) {
  ctx2d.fillStyle = fill;
  ctx2d.beginPath();
  ctx2d.roundRect(x, y + 4, w, h - 8, 6);
  ctx2d.fill();
  ctx2d.fillStyle = wave;
  const mid = y + h / 2;
  for (let i = 0; i < w; i++) {
    const p = peaks[i] || 0;
    const ph = Math.max(1, p * (h - 20));
    ctx2d.fillRect(x + i, mid - ph / 2, 1, ph);
  }
}

function MiniFader({ label, color, value, onChange, readout, readoutClass = '', testId, readoutTestId, title }) {
  return (
    <div className="flex flex-col items-center gap-1" title={title}>
      <span className={`text-[7px] font-bold leading-none ${color}`}>{label}</span>
      <div className="h-8 flex justify-center">
        <Slider
          orientation="vertical"
          value={[value]}
          onValueChange={(v) => onChange(v[0])}
          max={100}
          step={1}
          data-testid={testId}
        />
      </div>
      <span className={`text-[7px] font-mono tabular-nums leading-none text-[#888] ${readoutClass}`} data-testid={readoutTestId}>
        {readout}
      </span>
    </div>
  );
}

/** Vocal lane header: volume + reverb + echo mini faders and a mute toggle. */
function VocalHeader({ volumes, mutes, setVolumes, setMutes, reverb, setReverb, delay, setDelay }) {
  const muted = mutes.vocal;
  const v = volumes.vocal;
  const dbText = muted ? 'MUTE' : v <= 0.001 ? '-∞ dB' : `${(20 * Math.log10(v)).toFixed(1)} dB`;
  return (
    <div
      className="flex flex-col py-1 border-b border-[#1a1a1a]"
      style={{ height: LANE_H }}
      data-testid="channel-vocal-header"
    >
      <div className="flex items-center justify-center gap-1">
        <span className="text-[9px] font-bold tracking-wide text-red-400">VOCAL</span>
        <button
          onClick={() => setMutes((p) => ({ ...p, vocal: !p.vocal }))}
          className={`p-0.5 rounded ${muted ? 'text-red-400' : 'text-[#A1A1AA] hover:text-white'}`}
          aria-label="Mute VOCAL"
          data-testid="channel-vocal-mute"
        >
          {muted ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
        </button>
      </div>
      <div className="flex flex-1 items-center justify-center gap-2.5">
        <MiniFader
          label="VOL" color="text-red-400"
          value={v * 100} onChange={(x) => setVolumes((p) => ({ ...p, vocal: x / 100 }))}
          readout={dbText} readoutClass={muted ? 'text-red-400' : ''}
          testId="channel-vocal-volume" readoutTestId="channel-vocal-db"
          title="Vocal volume"
        />
        <MiniFader
          label="VERB" color="text-sky-400"
          value={reverb * 100} onChange={(x) => setReverb(x / 100)}
          readout={`${Math.round(reverb * 100)}%`}
          testId="channel-vocal-reverb" readoutTestId="channel-vocal-reverb-readout"
          title="Vocal reverb — heard on playback and baked into the mixdown. The exported vocal stem stays dry."
        />
        <MiniFader
          label="ECHO" color="text-amber-400"
          value={delay * 100} onChange={(x) => setDelay(x / 100)}
          readout={`${Math.round(delay * 100)}%`}
          testId="channel-vocal-delay" readoutTestId="channel-vocal-delay-readout"
          title="Dub echo on the vocal — heard on playback and baked into the mixdown. The exported vocal stem stays dry."
        />
      </div>
    </div>
  );
}

/** Vertical channel fader + mute, pinned to the left of its timeline lane. */
function TrackHeader({ label, color, volume, muted, onVolume, onMute, testId }) {
  const dbText = muted ? 'MUTE' : volume <= 0.001 ? '-∞ dB' : `${(20 * Math.log10(volume)).toFixed(1)} dB`;
  return (
    <div
      className="flex flex-col items-center justify-between py-1 border-b border-[#1a1a1a]"
      style={{ height: LANE_H }}
      data-testid={`${testId}-header`}
    >
      <span className={`text-[9px] font-bold tracking-wide ${color}`}>{label}</span>
      <Slider
        orientation="vertical"
        value={[volume * 100]}
        onValueChange={(v) => onVolume(v[0] / 100)}
        max={100}
        step={1}
        className="h-8"
        data-testid={`${testId}-volume`}
      />
      <span
        className={`text-[8px] font-mono tabular-nums leading-none ${muted ? 'text-red-400' : 'text-[#888]'}`}
        title="Channel volume"
        data-testid={`${testId}-db`}
      >
        {dbText}
      </span>
      <button
        onClick={onMute}
        className={`p-0.5 rounded ${muted ? 'text-red-400' : 'text-[#A1A1AA] hover:text-white'}`}
        aria-label={`Mute ${label}`}
        data-testid={`${testId}-mute`}
      >
        {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

export function Timeline({ engine }) {
  const { instrumental, vocal, position, duration, isRecording, seek, setInstrumentalOffset,
    liveVocalPeaksRef, recordStartPosRef, pushUndo,
    volumes, mutes, setVolumes, setMutes,
    reverb, setReverb, delay, setDelay,
    splitAt, eraseSplitSide, cancelSplit } = engine;
  const canvasRef = useRef(null);
  const scrollRef = useRef(null);
  const dragRef = useRef(null);
  const pressRef = useRef(null); // pending long-press on touch
  const pointersRef = useRef(new Map()); // pointerId -> { x, y }  (touch only)
  const pinchRef = useRef(null); // { initDist, initZoom, midSec, midClientX }
  const [, forceDraw] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [dragActive, setDragActive] = useState(false);

  const pxPerSec = BASE_PX_PER_SEC * zoom;
  const totalSec = Math.max(duration + 15, 90);
  const width = Math.ceil(totalSec * pxPerSec);
  const height = RULER_H + LANE_H * 2;

  const instDur = instrumental ? instrumental.buffer.duration : 0;
  const instPeaks = useMemo(
    () => (instrumental ? computePeaks(instrumental.buffer, Math.ceil(instDur * pxPerSec), instDur) : null),
    [instrumental, pxPerSec, instDur]
  );
  const vocalPeaks = useMemo(
    () => (vocal ? computePeaks(vocal.buffer, Math.ceil(vocal.buffer.duration * pxPerSec)) : null),
    [vocal, pxPerSec]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const c = canvas.getContext('2d');
    c.setTransform(dpr, 0, 0, dpr, 0, 0);

    // background
    c.fillStyle = '#0a0a0a';
    c.fillRect(0, 0, width, height);

    // ruler
    c.fillStyle = '#111';
    c.fillRect(0, 0, width, RULER_H);
    c.fillStyle = '#555';
    c.font = '10px monospace';
    // At high zoom, tick every 1s; at low zoom, tick every 10s
    const tickEvery = zoom >= 2 ? 1 : zoom >= 1 ? 5 : 10;
    for (let s = 0; s <= totalSec; s += tickEvery) {
      const x = s * pxPerSec;
      c.fillRect(x, RULER_H - 8, 1, 8);
      c.fillText(`${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`, x + 3, RULER_H - 10);
    }

    // lanes
    const laneY = (i) => RULER_H + i * LANE_H;
    [0, 1].forEach((i) => {
      c.fillStyle = i === 0 ? '#0d0f08' : '#0f0808';
      c.fillRect(0, laneY(i), width, LANE_H);
      c.strokeStyle = '#1e1e1e';
      c.strokeRect(0, laneY(i), width, LANE_H);
    });

    // clips
    if (instrumental && instPeaks) {
      const fill = dragActive ? 'rgba(205,220,57,0.28)' : 'rgba(205,220,57,0.14)';
      drawClip(c, instPeaks, instrumental.offset * pxPerSec, laneY(0),
        Math.ceil(instDur * pxPerSec), LANE_H, fill, LIME);
    }
    if (vocal && vocalPeaks) {
      drawClip(c, vocalPeaks, vocal.offset * pxPerSec, laneY(1),
        Math.ceil(vocal.buffer.duration * pxPerSec), LANE_H, 'rgba(239,68,68,0.14)', '#ef4444');
    }
    // live recording region — draw peaks from the mic analyser as they come in
    if (isRecording) {
      const startSec = recordStartPosRef?.current ?? position;
      const startX = startSec * pxPerSec;
      const curX = position * pxPerSec;
      const w = Math.max(2, curX - startX);
      c.fillStyle = 'rgba(239,68,68,0.14)';
      c.beginPath();
      c.roundRect(startX, laneY(1) + 4, w, LANE_H - 8, 6);
      c.fill();
      const peaks = liveVocalPeaksRef?.current || [];
      const mid = laneY(1) + LANE_H / 2;
      c.fillStyle = '#ef4444';
      const cols = Math.min(peaks.length, Math.ceil(w));
      for (let i = 0; i < cols; i++) {
        const p = peaks[i] || 0;
        const ph = Math.max(1, p * (LANE_H - 20));
        c.fillRect(startX + i, mid - ph / 2, 1, ph);
      }
      c.fillStyle = 'rgba(239,68,68,0.6)';
      c.fillRect(curX - 1, laneY(1) + 4, 2, LANE_H - 8);
    }

    // armed split/cut — highlight both halves of the instrumental so the
    // artist can tap the side they want to erase
    if (instrumental && splitAt != null) {
      const clipX = instrumental.offset * pxPerSec;
      const clipW = Math.ceil(instDur * pxPerSec);
      const cutX = splitAt * pxPerSec;
      if (cutX > clipX && cutX < clipX + clipW) {
        c.fillStyle = 'rgba(239,68,68,0.12)';
        c.fillRect(clipX, laneY(0) + 4, cutX - clipX, LANE_H - 8);
        c.fillRect(cutX, laneY(0) + 4, clipX + clipW - cutX, LANE_H - 8);
        c.fillStyle = '#fff';
        c.fillRect(cutX - 1, laneY(0), 2, LANE_H);
        c.font = 'bold 10px sans-serif';
        c.textAlign = 'center';
        c.fillStyle = 'rgba(255,255,255,0.9)';
        if (cutX - clipX > 80) c.fillText('✕ TAP TO ERASE', (clipX + cutX) / 2, laneY(0) + LANE_H - 12);
        if (clipX + clipW - cutX > 80) c.fillText('✕ TAP TO ERASE', (cutX + clipX + clipW) / 2, laneY(0) + LANE_H - 12);
        c.textAlign = 'left';
      }
    }

    // playhead
    c.fillStyle = '#fff';
    c.fillRect(position * pxPerSec, 0, 1.5, height);
    c.fillStyle = LIME;
    c.beginPath();
    c.moveTo(position * pxPerSec - 5, 0);
    c.lineTo(position * pxPerSec + 6.5, 0);
    c.lineTo(position * pxPerSec + 0.75, 8);
    c.fill();
  }, [instrumental, vocal, instPeaks, vocalPeaks, position, width, height, totalSec, pxPerSec, zoom, isRecording, dragActive, instDur, splitAt]);

  // keep playhead visible while playing
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const x = position * pxPerSec;
    if (x < el.scrollLeft + 40 || x > el.scrollLeft + el.clientWidth - 80) {
      el.scrollLeft = Math.max(0, x - el.clientWidth / 3);
    }
  }, [position, pxPerSec]);

  const posFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const clearPress = () => {
    if (pressRef.current) {
      clearTimeout(pressRef.current.timer);
      pressRef.current = null;
    }
  };

  const pointerDistance = () => {
    const arr = Array.from(pointersRef.current.values());
    if (arr.length < 2) return 0;
    const dx = arr[0].x - arr[1].x;
    const dy = arr[0].y - arr[1].y;
    return Math.hypot(dx, dy);
  };

  const activateDrag = useCallback((pointerId, startX, startOffset) => {
    dragRef.current = { startX, startOffset };
    setDragActive(true);
    try { canvasRef.current.style.touchAction = 'none'; } catch (e) { /* noop */ }
    try { canvasRef.current.setPointerCapture(pointerId); } catch (e) { /* noop */ }
    try { navigator.vibrate?.(18); } catch (e) { /* noop */ }
  }, []);

  const onPointerDown = (e) => {
    const { x, y } = posFromEvent(e);
    const sec = x / pxPerSec;

    // Track every touch pointer so we can detect pinch (2 fingers on canvas).
    if (e.pointerType === 'touch') {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointersRef.current.size === 2) {
        // Pinch begins — cancel any pending drag/long-press, hijack touch-action.
        clearPress();
        dragRef.current = null;
        setDragActive(false);
        try { canvasRef.current.style.touchAction = 'none'; } catch (err) { /* noop */ }
        pinchRef.current = { initDist: pointerDistance() || 1, initZoom: zoom };
        return;
      }
    }

    // Split armed — the tap is handled on click (erase a side / cancel);
    // suppress seek, drag and long-press until the cut is resolved.
    if (splitAt != null) return;

    const hitInst = instrumental
      && y >= RULER_H && y < RULER_H + LANE_H
      && sec >= instrumental.offset
      && sec <= instrumental.offset + instDur;

    if (hitInst) {
      if (e.pointerType === 'touch') {
        // Wait for long-press before grabbing — otherwise let the browser scroll.
        const pointerId = e.pointerId;
        const startX = x;
        const startOffset = instrumental.offset;
        const timer = setTimeout(() => {
          if (!pressRef.current) return;
          pressRef.current = null;
          activateDrag(pointerId, startX, startOffset);
        }, LONG_PRESS_MS);
        pressRef.current = { pointerId, timer, startX: x, startY: y };
        return;
      }
      // Mouse / pen — immediate drag (existing behavior)
      e.preventDefault();
      activateDrag(e.pointerId, x, instrumental.offset);
      return;
    }
    // Anywhere else: seek to that time. Only for mouse/pen so touch can pan the grid.
    if (e.pointerType !== 'touch') {
      e.preventDefault();
      seek(sec);
    }
  };

  const onPointerMove = (e) => {
    // Keep the touch-pointer map in sync so pinch tracking sees fresh coords.
    if (e.pointerType === 'touch' && pointersRef.current.has(e.pointerId)) {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }
    // Pinch-zoom takes precedence over any other gesture.
    if (pinchRef.current && pointersRef.current.size >= 2) {
      const d = pointerDistance();
      if (d > 0) {
        const ratio = d / pinchRef.current.initDist;
        const next = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, pinchRef.current.initZoom * ratio));
        setZoom(next);
      }
      return;
    }
    const p = pressRef.current;
    if (p) {
      // Still waiting for long-press — if user drags too much, cancel and let scroll take over.
      const { x, y } = posFromEvent(e);
      if (Math.abs(x - p.startX) > LONG_PRESS_MOVE_TOL || Math.abs(y - p.startY) > LONG_PRESS_MOVE_TOL) {
        clearPress();
      }
      return;
    }
    if (!dragRef.current) return;
    const { x } = posFromEvent(e);
    const delta = (x - dragRef.current.startX) / pxPerSec;
    setInstrumentalOffset(Math.max(0, dragRef.current.startOffset + delta));
    forceDraw((n) => n + 1);
  };

  const onPointerUp = (e) => {
    if (e && e.pointerType === 'touch') {
      pointersRef.current.delete(e.pointerId);
      if (pinchRef.current && pointersRef.current.size < 2) {
        pinchRef.current = null;
        try { canvasRef.current.style.touchAction = 'pan-x'; } catch (err) { /* noop */ }
        // Don't fall through into drag/seek logic — this was a pinch gesture.
        return;
      }
    }
    clearPress();
    const d = dragRef.current;
    if (d && instrumental && Math.abs(instrumental.offset - d.startOffset) > 0.001) {
      pushUndo({ instrumental: { ...instrumental, offset: d.startOffset }, vocal });
    }
    dragRef.current = null;
    setDragActive(false);
    try { canvasRef.current.style.touchAction = 'pan-x'; } catch (err) { /* noop */ }
  };

  // Handle a single-tap on touch to seek (long-press activates drag, a plain tap seeks).
  const onCanvasClick = (e) => {
    if (dragRef.current || dragActive) return;
    const { x, y } = posFromEvent(e);
    if (splitAt != null) {
      const sec = x / pxPerSec;
      const onClip = instrumental
        && y >= RULER_H && y < RULER_H + LANE_H
        && sec >= instrumental.offset && sec <= instrumental.offset + instDur;
      if (onClip) eraseSplitSide(sec < splitAt ? 'left' : 'right');
      else cancelSplit();
      return;
    }
    seek(x / pxPerSec);
  };

  const canZoomIn = zoom < ZOOM_MAX - 0.001;
  const canZoomOut = zoom > ZOOM_MIN + 0.001;

  const zoomIn = () => {
    const next = ZOOM_LEVELS.find((l) => l > zoom + 0.001);
    if (next !== undefined) setZoom(next);
  };
  const zoomOut = () => {
    const next = [...ZOOM_LEVELS].reverse().find((l) => l < zoom - 0.001);
    if (next !== undefined) setZoom(next);
  };

  const zoomLabel = Number.isInteger(zoom) ? `${zoom}x` : `${zoom.toFixed(1)}x`;

  return (
    <div className="rounded-xl border border-[#262626] bg-[#0a0a0a] select-none overflow-hidden" data-testid="studio-timeline-wrap">
      <div className="flex items-center justify-between px-2 py-1 border-b border-[#1a1a1a] bg-[#0d0d0d]">
        <div className="flex items-center gap-1.5 text-[10px] text-[#666]">
          <Hand className="w-3 h-3" />
          <span className="hidden sm:inline">Hold to move · drag to scrub</span>
          <span className="sm:hidden">Hold to move · pinch to zoom</span>
        </div>
        <div className="flex items-center gap-1" data-testid="timeline-zoom-controls">
          <button
            onClick={zoomOut}
            disabled={!canZoomOut}
            className="w-6 h-6 flex items-center justify-center rounded bg-[#141414] text-[#aaa] hover:text-lime disabled:opacity-40 disabled:hover:text-[#aaa] border border-[#222]"
            aria-label="Zoom out"
            data-testid="timeline-zoom-out"
          >
            <ZoomOut className="w-3.5 h-3.5" />
          </button>
          <span className="text-[10px] text-[#888] tabular-nums w-10 text-center" data-testid="timeline-zoom-level">{zoomLabel}</span>
          <button
            onClick={zoomIn}
            disabled={!canZoomIn}
            className="w-6 h-6 flex items-center justify-center rounded bg-[#141414] text-[#aaa] hover:text-lime disabled:opacity-40 disabled:hover:text-[#aaa] border border-[#222]"
            aria-label="Zoom in"
            data-testid="timeline-zoom-in"
          >
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="flex">
        <div className="shrink-0 w-24 bg-[#0d0d0d] border-r border-[#1a1a1a]" data-testid="timeline-track-headers">
          <div style={{ height: RULER_H }} className="border-b border-[#1a1a1a] bg-[#111]" />
          <TrackHeader
            label="INSTR" color="text-lime" testId="channel-inst"
            volume={volumes.inst} muted={mutes.inst}
            onVolume={(v) => setVolumes((p) => ({ ...p, inst: v }))}
            onMute={() => setMutes((p) => ({ ...p, inst: !p.inst }))}
          />
          <VocalHeader
            volumes={volumes} mutes={mutes} setVolumes={setVolumes} setMutes={setMutes}
            reverb={reverb} setReverb={setReverb} delay={delay} setDelay={setDelay}
          />
        </div>
        <div ref={scrollRef} className="overflow-x-auto flex-1 min-w-0" data-testid="studio-timeline">
          <canvas
            ref={canvasRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onClick={onCanvasClick}
            style={{ touchAction: 'pan-x', cursor: instrumental ? (dragActive ? 'grabbing' : 'grab') : 'pointer', display: 'block' }}
          />
        </div>
      </div>
    </div>
  );
}
